import { useState, useMemo } from 'react';
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiSearch, FiSlash, FiCheck } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useAllUsers, banUser, unbanUser } from '../../hooks/useAdmin';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { formatRelativeTime } from '../../utils/formatters';

export default function AdminUsers() {
  const { users, loading } = useAllUsers();
  const [search, setSearch] = useState('');
  const toast = useToast();

  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter(
      (u) =>
        u.anonymizedUsername?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q)
    );
  }, [users, search]);

  const handleToggleBan = async (user) => {
    try {
      if (user.isBanned) {
        await unbanUser(user.id);
        toast({ title: 'User unbanned', status: 'success', duration: 3000, isClosable: true });
      } else {
        await banUser(user.id);
        toast({ title: 'User banned', status: 'warning', duration: 3000, isClosable: true });
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 4000, isClosable: true });
    }
  };

  if (loading) return <LoadingSpinner message="Loading users..." />;

  return (
    <VStack spacing={6} align="stretch">
      <HStack justify="space-between" align="center">
        <Box>
          <Heading size="lg">Users</Heading>
          <Text color="gray.500" mt={1}>{users.length} total users</Text>
        </Box>
      </HStack>

      <InputGroup maxW="400px">
        <InputLeftElement pointerEvents="none">
          <Icon as={FiSearch} color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder="Search by username or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </InputGroup>

      <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} overflow="hidden">
        <Box overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Username</Th>
                <Th>Email</Th>
                <Th>Reputation</Th>
                <Th>Status</Th>
                <Th>Joined</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtered.map((user) => (
                <Tr key={user.id}>
                  <Td>
                    <Text
                      as={Link}
                      to={`/admin/users/${user.id}`}
                      fontWeight="medium"
                      color="orange.500"
                      _hover={{ textDecoration: 'underline' }}
                    >
                      {user.anonymizedUsername || 'No username'}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color="gray.600">{user.email}</Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm">
                      {user.reputation?.rating ? `${user.reputation.rating.toFixed(1)} stars` : 'N/A'}
                      {user.reputation?.completedBounties > 0 && ` (${user.reputation.completedBounties} completed)`}
                    </Text>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      {user.isAdmin && <Badge colorScheme="orange" fontSize="xs">Admin</Badge>}
                      {user.isBanned && <Badge colorScheme="red" fontSize="xs">Banned</Badge>}
                      {!user.isAdmin && !user.isBanned && <Badge colorScheme="green" fontSize="xs">Active</Badge>}
                    </HStack>
                  </Td>
                  <Td>
                    <Text fontSize="xs" color="gray.500">{formatRelativeTime(user.createdAt)}</Text>
                  </Td>
                  <Td>
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme={user.isBanned ? 'green' : 'red'}
                      leftIcon={<Icon as={user.isBanned ? FiCheck : FiSlash} />}
                      onClick={() => handleToggleBan(user)}
                    >
                      {user.isBanned ? 'Unban' : 'Ban'}
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
        {filtered.length === 0 && (
          <Box py={8} textAlign="center">
            <Text color="gray.500">No users found</Text>
          </Box>
        )}
      </Box>
    </VStack>
  );
}
